import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { RoleRouteObject, RouteFunctionParams } from '@/types'
import { defer, LoaderFunction } from 'react-router-typesafe'
import TicketEditor from '@/routes/tickets/TicketEditor'
import { ODataModelResponseV4 } from '@odata2ts/odata-core'
import { HttpResponseModel } from '@odata2ts/http-client-api'
import { argomento_problema, ticket, azienda } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { getAzureUserInformation } from '@/auth/azure/azureManager'
import { StatoTicket } from '@/routes/tickets/TicketEditor.enums'
import { Roles } from '@/auth/azure/Roles'
import { Azienda, Reparto } from '@/enums'
import { Qticket } from '@/model/qsadminapi/QQsAdminApiModule'
import { fetchAllProblemArguments } from '@/routes/tickets/ticketDetail'

export type TicketEditorLoaderData = {
  ticket: ticket
  problemArguments: argomento_problema[] | undefined
}

export const ticketEditorRouteLoader = (async ({ request, params }: RouteFunctionParams<'ticketId' | 'clientId'>) => {
  const ticketId = params.ticketId
  const clientId = params.clientId
  const qsAdminApi = useQsAdminApiManager.getState().service

  let azienda: HttpResponseModel<ODataModelResponseV4<azienda>> | undefined
  if (clientId)
    azienda = await qsAdminApi.azienda(Number(clientId)).query((builder, azienda) => {
      builder.select('id', 'nome')
    })
  const titlePrefix = azienda ? `${azienda.data.nome} - ` : ``

  // Fire all dropdown queries immediately - they all run in parallel
  const getClients = qsAdminApi.azienda().query((builder, azienda) => {
    builder.filter(azienda.sedi.any())
    builder.filter(clientId ? azienda.id.eq(Number(clientId)) : null)
    builder.orderBy(azienda.nome.asc())
    builder.select('id', 'nome')
  })

  const getStates = qsAdminApi.stato_ticket().query((builder, stato) => {
    builder.select('id', 'nome')
    builder.orderBy(stato.nome.asc())
  })

  const getCategories = qsAdminApi.tipologia_ticket().query((builder, categoria) => {
    builder.select('id', 'nome')
    builder.orderBy(categoria.nome.asc())
  })

  const getPlatforms = qsAdminApi.piattaforma_ticket().query((builder, piattaforma) => {
    builder.select('id', 'nome', 'note')
    builder.orderBy(piattaforma.nome.asc())
  })

  const getSupportApplications = qsAdminApi.qs_supporto_applicazione().query((builder, applicazione) => {
    builder.select('id', 'nome', 'note')
    builder.orderBy(applicazione.nome.asc())
  })

  const getProblemCategories = qsAdminApi.tipologia_problema().query((builder, tipologiaProbl) => {
    builder.select('id', 'nome', 'note')
    builder.orderBy(tipologiaProbl.nome.asc())
  })

  const getEmployees = qsAdminApi.user().query((builder, impiegato) => {
    builder.filter(
      impiegato.attivo
        .eq(true)
        .and(impiegato.reparto.props.id.eq(Reparto.SVILUPPO).or(impiegato.reparto.props.id.eq(Reparto.TECNICO)))
        .and(impiegato.azienda.props.id.eq(Azienda.QS))
        .or(ticketId ? impiegato.tickets.any((ticket: Qticket) => ticket.id.eq(Number(ticketId))) : null),
    )
    builder.select('id', 'fullname', 'email')
    builder.orderBy(impiegato.fullname.asc())
  })

  let getTicketData: Promise<TicketEditorLoaderData | null>

  if (ticketId) {
    getTicketData = Promise.resolve(null)
  } else {
    // Creator path: build default ticket (these small queries are fast)
    getTicketData = (async () => {
      const [getStatoAperto, getTipologiaDefault, problemArguments] = await Promise.all([
        qsAdminApi.stato_ticket(StatoTicket.APERTO).query((builder, stato) => {
          builder.select('id', 'nome')
        }),
        qsAdminApi.tipologia_ticket().query((builder, tipologia) => {
          builder.filter(tipologia.nome.eq('ASSISTENZA TECNICA'))
          builder.select('id', 'nome')
        }),
        fetchAllProblemArguments(),
      ])
      const ticket: ticket = {
        id: 0,
        ded_Dis: '',
        ded_RootFam: '',
        ded_Id: 0,
        contattato: false,
        attesa: false,
        urgente: false,
        notifica_chiusura: false,
        rating: 0,
        rating_note: null,
        apertura: new Date().toISOString(),
        chiusura: null,
        descrizione: null,
        soluzione: null,
        leg_tag1: null,
        leg_tag2: null,
        durata: 0,
        leg_contatto: null,
        leg_n_chiamate: 0,
        leg_email_contatto: null,
        leg_phone_contatto: null,
        assegnato: null,
        stato: getStatoAperto.data,
        attachmentsFolderUrl: '',
        sede: null,
        tipologia_ticket: getTipologiaDefault.data.value[0],
        customer_id: null,
        piattaforma: null,
        applicazione: null,
        tipologia_problema: null,
        argomento_problema: null,
        email_addizionali: [],
        dumps: [],
      }
      return { ticket, problemArguments }
    })()
  }

  return defer({
    titlePrefix,
    getTicketData,
    getClients,
    getStates,
    getCategories,
    getPlatforms,
    getSupportApplications,
    getProblemCategories,
    getEmployees,
    getUserInfo: getAzureUserInformation(),
    defaultCRUDAllowedRoles: [Roles.GlobalAdministrator, Roles.Administrator, Roles.Supervisor, Roles.TechDeveloper],
  })
}) satisfies LoaderFunction

const TicketEditorRoute = {
  path: 'details',
  element: <TicketEditor creating={false} />,
  loader: ticketEditorRouteLoader,
  allowedRoles: [Roles.Guest],
  handle: {
    crumb: (match) => ({
      label: 'Dettagli',
    }),
  },
} as RoleRouteObject

export default TicketEditorRoute
